import * as React from 'react'

import { StoreSearch } from '@thg-commerce/enterprise-components/StoreSearch'
import {
  AutocompletePrediction,
  initAutocomplete,
} from '@thg-commerce/enterprise-core'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { AutocompletePredictions } from '../../components/AutocompletePredictions'

interface StoreSearchRendererProps {
  alignment: HorizontalAlignment
  i18nText: {
    title: string
    subtitle: string
    inputLabel: string
    buttonLabel: string
    infoLabel?: string
    infoLinkLabel?: string
  }
  isSearchReady?: boolean
  getNearbyStores?: (variables: {
    variables: {
      longitude: number
      latitude: number
      limit: number
    }
  }) => void
  setResultsFor?: React.Dispatch<React.SetStateAction<string>>
  setSelectedNearbyStore?: React.Dispatch<React.SetStateAction<number>>
  onChangeRedirect?: (value: string) => void
  locationParam?: string
  showInfoLabel?: boolean
}

export const StoreSearchRenderer = (props: StoreSearchRendererProps) => {
  const {
    isSearchReady,
    getNearbyStores,
    setSelectedNearbyStore,
    setResultsFor,
    ...otherProps
  } = props
  const [autocompletePredictions, setAutocompletePredictions] = React.useState<
    AutocompletePrediction[]
  >([])
  const [showGeolocation, setShowGeolocation] = React.useState<boolean>(false)
  const [searchInputOverride, setSearchInputOverride] = React.useState<string>(
    props.locationParam || '',
  )
  const [selectedStoreLocation, setSelectedStoreLocation] = React.useState<
    | {
        latitude: number
        longitude: number
      }
    | undefined
  >(undefined)
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSelectedStoreLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setSearchInputOverride(
            `${position.coords.latitude} ${position.coords.longitude}`,
          )
          setShowGeolocation(false)
          setAutocompletePredictions([])
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setShowGeolocation(false)
          }
        },
      )
    }
  }

  const onSearchStoreHandler = React.useCallback(
    (inputValue?: string) => {
      if (
        isSearchReady &&
        ((searchInputOverride && searchInputOverride.trim()) || inputValue)
      ) {
        const options = inputValue
          ? {
              address: inputValue,
              componentRestrictions: { country: 'uk' },
              region: 'uk',
            }
          : {
              address: searchInputOverride,
            }
        const geocoder = new google.maps.Geocoder()
        geocoder.geocode(options, (results, status) => {
          if (status === 'OK') {
            if (results && results[0] && results[0].geometry.location) {
              const location = results[0].geometry.location
              setSelectedStoreLocation({
                latitude: location.lat(),
                longitude: location.lng(),
              })
              getNearbyStores &&
                getNearbyStores({
                  variables: {
                    latitude: location.lat(),
                    longitude: location.lng(),
                    limit: 20,
                  },
                })
              setSelectedNearbyStore && setSelectedNearbyStore(0)
              setResultsFor && setResultsFor(results[0].formatted_address)
            }
          }
        })
      }
    },
    [
      searchInputOverride,
      isSearchReady,
      getNearbyStores,
      setSelectedNearbyStore,
      setResultsFor,
    ],
  )

  React.useEffect(() => {
    onSearchStoreHandler()
  }, [otherProps.locationParam, isSearchReady, onSearchStoreHandler])

  return (
    <React.Fragment>
      <StoreSearch
        i18nText={otherProps.i18nText}
        alignment={otherProps.alignment}
        searchInputOverride={searchInputOverride}
        selectedStoreLocation={selectedStoreLocation}
        showInfoLabel={otherProps.showInfoLabel}
        onChange={(event) => {
          event.target?.value && isSearchReady
            ? initAutocomplete(event.target.value, setAutocompletePredictions)
            : setAutocompletePredictions([])
        }}
        onFocus={() => setShowGeolocation(true)}
        clearInputValueHandler={() => setAutocompletePredictions([])}
        closeDropdown={() => {
          setShowGeolocation(false)
          setAutocompletePredictions([])
        }}
        setSearchInputOverride={setSearchInputOverride}
        searchStoreHandler={(value) =>
          !otherProps.onChangeRedirect
            ? onSearchStoreHandler(value)
            : otherProps.onChangeRedirect(searchInputOverride || value)
        }
        {...{
          autocompletePredictions: (
            <AutocompletePredictions
              setSearchInputOverride={setSearchInputOverride}
              setAutocompletePredictions={setAutocompletePredictions}
              setShowGeolocation={setShowGeolocation}
              getUserLocation={getUserLocation}
              showGeolocation={showGeolocation}
              predictions={autocompletePredictions}
            />
          ),
        }}
      />
    </React.Fragment>
  )
}
